import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../modules/material/material.module';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
@Component({
  selector: 'huge-monorepo-header',
  standalone: true,
  imports: [CommonModule, MaterialModule, NgxSkeletonLoaderModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})

export class HeaderComponent {

  /**
   * Configuration for the header
   * @type {HeaderConfig}
   * @memberof HeaderComponent
   */
  @Input() config!: HeaderConfig;

  data = [];

  showData(e: any, item: any) {
    if (this.isObjectWithCurrentValue(item) && item.hoverValue) {
      item.currentValue = item.hoverValue; // Switch to hoverValue
    }
  
    if (!e.target.parentElement.classList.contains('display-full-text')) {
      e.target.parentElement.classList.add('display-full-text');
      if (!e.target.classList.contains('display-full-text-child')) {
        e.target.classList.add('display-full-text-child');
      }
    }
  }
  
  hideData(e: any, item: any) {
    if (this.isObjectWithCurrentValue(item) && item.hoverValue) {
      item.currentValue = item.value; // Revert to original value
    }
  
    if (e.target.parentElement.classList.contains('display-full-text')) {
      e.target.parentElement.classList.remove('display-full-text');
      if (e.target.classList.contains('display-full-text-child')) {
        e.target.classList.remove('display-full-text-child');
      }
    }
  }

  public isString(value: any): value is string {
    return typeof value === 'string';
  }
  public isNumber(value: any): value is number {
    return typeof value === 'number';
  }

  public isIcon(value: any): value is { text: string, isIcon: boolean, clickable?: boolean, action?: () => any } {
    if (value.isIcon === undefined) {
      return false;
    }
    return value.isIcon;
  }

  public extractValue(value: any): string {
    return value.text;
  }

  public isButton(value: any): value is button {
    return value.options === undefined && value.action !== undefined;
  }

  public isBreak(value: any): value is { break: true } {
    return value != null && value.break === true;
  }

  public getImages(value: any): image[] {
    return value;
  }

  public isLayered(value: any): value is layered {
    if (value.topText !== undefined || value.middleText !== undefined || value.bottomText !== undefined) {
      return true;
    }

    return false;
  }

  public isDotted(value: any): value is { value: string | textWithIcon[], dot: 'gray' | 'red' | 'green' | 'blue' | 'yellow' | 'crimson' | string } {
    return value.dot !== undefined;
  }

  public getTextWithIcon(value: any): textWithIcon[] {
    return value as textWithIcon[];
  }

  public isHeading(value: any): value is { heading: string | (string | { path: string, height?: string, width?: string })[] } {
    return value.heading !== undefined;
  }

  public isSubheading(value: any): value is { subheading: string | (string | { path: string, height?: string, width?: string })[] } {
    return value.subheading !== undefined;
  }

  public getSignalIcon(value: any): any {

    let signalStrengthPath: any;
    switch (value) {
      case "Excellent":
        signalStrengthPath = "../assets/Teltonika/connection_signal full bar.svg";
        break;
      case "Good":
        signalStrengthPath = "../assets/Teltonika/connection_signal 3 bar.svg";
        break;
      case "Fair":
        signalStrengthPath = "../assets/Teltonika/connection_signal 2 bar.svg";
        break;
      case "Poor":
        signalStrengthPath = "../assets/Teltonika/connection_signal 1 bar.svg";
        break;
      case "No Signal":
        signalStrengthPath = "../assets/Teltonika/connection_signal empty.svg";
        break;
    }
    return signalStrengthPath;
  }

  public showPopover(event: MouseEvent, imageData: image, allImages: image[]) {
    allImages.forEach((image) => {
      if (image.popover != null && image !== imageData) {
        image.displayPopover = false;
      }
    });
    if (imageData.popover == null) {
      return;
    }

    const x = event.clientX;
    const midPointX = document.documentElement.clientWidth / 2;

    imageData.popoverSide = x >= midPointX ? 'left' : 'right';
    if (imageData.displayPopover == null) {
      imageData.displayPopover = true;
    }
    imageData.displayPopover = !imageData.displayPopover;
  }

  public isDetails(value: any): value is { [title: string]: string } {
    return true;
  }

  public getAbove(value: any): (textWithIcon[])[] {

    if (value === undefined) {
      return [];
    }
    const list: (textWithIcon[])[] = [];
    for (let i = 0; i < value.length; i++) {
      if (value[i].position === "above") {
        list.push(value[i].value);
      }
    }

    return list;
  }

  public getBelow(value: any): { text: textWithIcon[] }[] {
    if (value === undefined) {
      return [];
    }
    const values: { text: textWithIcon[] }[] = [];
    for (let i = 0; i < value.length; i++) {
      if (value[i].position === "below") {
        values.push({ text: value[i].text });
      }
    }
    return values;
  }

  orderFunction(functions?: { [title: string]: (a: any, b: any) => number }, key?: any): (a: any, b: any) => number {
    if (functions != null && key != null) {
      return functions[key];
    }
    return (a: any, b: any) => {
      if (a.key < b.key) return 1;
      if (a.key > b.key) return -1;
      return 0;
    }
  }

  isPopoverDataObject(val: any): val is { [key: string]: ((string | { path: string, height?: string, width?: string })[]) } {
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      return true;
    }
    return false;
  }

  isArray(val: any): val is any[] {
    return Array.isArray(val);
  }

  isImage(val: any): val is image {
    return val.path !== undefined;
  }

  isObjectWithCurrentValue(obj: any): obj is { value: string; hoverValue?: string | null; currentValue?: string | null } {
    return obj && typeof obj === 'object' && 'value' in obj;
  }

}
/**
 * Used to parse images into the header.
 * The path is the path to the image, and the popover is an array of objects that will be displayed when the image is clicked
 */
type image = { path: string, height?: string, width?: string, popover?: ({ heading?: string | (string | { path: string, height?: string, width?: string })[] } | { subheading?: string | (string | { path: string, height?: string, width?: string })[] } | { [title: string]: string | (string | { path: string, height?: string, width?: string })[] } | button)[], displayPopover?: boolean, popoverSide?: 'left' | 'right' };
/**
 * Is a string for normal text, or an object with a text property and an optional isIcon.
 * If isIcon is true, the text will be displayed as an icon
 */
type textWithIcon = string | { text: string, isIcon?: boolean, clickable?: boolean, action?: () => any };
/**
 * Dropdown object, with options for the dropdown, a title, an alt text and an optional icon
 */
type dropdown = {
  options: { text: string, action: () => any }[],
  title: string,
  alt: string,
  icon?: string,
};
/**
 * Button object, with text, icon, tooltip and action
 
 */
type button = {
  text?: string,
  icon?: string,
  tooltip?: string,
  action: () => any,
  visible? :boolean
}

/**
 * Layered object to display text in three layers
 */
type layered = { topText: string, middleText: string, bottomText: string };
/**
 * Configuration for the header
 */
export class HeaderConfig {
  /**
   * Smaller header section, often storing the title of the header along with actions that can be performed
   */
  actionHolder?: {
    /**
     * Heading to be displayed, can be a string or an object with a value and a dot color
     */
    heading: string | { value: string | textWithIcon[], tooltip: string, dot: 'gray' | 'red' | 'green' | 'blue' | 'yellow' | 'crimson' | string } | textWithIcon[],
    /**
     * Array of subHeadings to be displayed, each subHeading is an array of textWithIcon objects, and a position
     */
    subHeadings: { value: textWithIcon[], position: "above" | "below" }[],
    /**
     * Array of buttons to be displayed, each button is an object with a color, icon, tooltip and action
     * Buttons can also be dropdowns, or line breaks.
     */
    buttons?: (dropdown | button | { break: true })[],
  };
  details!: {
    /**
     * Key value pairs for the header, the key is the title to be displayed on top in larger text, and the value is the value to be displayed within the titles block
     */
    [title: string]: {
      /**
         * Key value pairs for the header, the key is the title to be displayed on the left, and the value is the value to be displayed on the right
         */
      [name: string]: {
        /**
         * Value to be displayed, can be a string, an array of textWithIcon, an array of images or a layered object
         */
        value: string | layered | textWithIcon[] | image[] | null,
         /**
         * The value to be displayed on hover (optional).
         */
        hoverValue?: string | null;
        
        /**
         * The current value to be displayed (optional).
         */
        currentValue?: string | null;
        /**
         * Boolean stating if the value is an image
         */
        image?: boolean,
        /**
         * color of the dot to be display
         */
        dot?: 'gray' | 'red' | 'green' | 'blue' | 'yellow' | 'crimson' | string,
      } | string | number | { break: true } | null,
    },
  };
  OrderFunctions?: {
    [title: string]: (a: any, b: any) => number
  }
}
